import { useId, useState, type ReactNode } from 'react';
import { useMemberOptions } from '@/hooks/queries';
import { Field, MoneyInput } from '@/components/ui';
import { LeadPicker } from '@/components/leads/LeadPicker';
import { LEVERAGE_LONG_LABELS, LEVERAGE_STATUS_LABELS, nowHHMM, todayISO } from '@/lib/format';
import {
  hasPasswordInNote, isAlavancagem, isDeposit, isGas, isTransfer, isUnaDeposit, LEVERAGE_STATUSES, RECEIPT_FORM_TYPES, requiresLead,
} from '@/lib/receipts';
import type { LeverageStatus, Receipt, ReceiptType } from '@/lib/types';

/** Campos do comprovante como estão no formulário (valores em centavos). */
export interface ReceiptDraft {
  description: string;
  soldAmount: number;
  leverageType: ReceiptType | '';
  leverageAmount: number;
  /** Depositado, na alavancagem. A buscada (`soldAmount`) só entra com ela realizada. */
  depositAmount: number;
  /** Situação da alavancagem; só vale na alavancagem. */
  leverageStatus: LeverageStatus;
  date: string;
  time: string;
  leadId: string;
  /** Só para mostrar o cliente escolhido; não vai para o servidor. */
  leadName: string;
  memberId: string;
  note: string;
}

const TYPE_OPTIONS = RECEIPT_FORM_TYPES.map((value) => ({ value, label: LEVERAGE_LONG_LABELS[value] ?? value }));

/**
 * A venda não se anexa mais: ela entra sozinha pela compra no checkout (Cakto,
 * Kirvano), e a venda em cripto, pelo carnê. A opção só aparece na edição de um
 * comprovante que já é de venda — ver assertNotSale no servidor.
 */
const SALE_CLOSED_NOTE = 'Venda não se anexa: ela entra sozinha pela compra no checkout (Cakto, Kirvano). Venda em cripto vai em Vendas em cripto.';

/** Os tipos sem produto ganham uma descrição pronta, que a pessoa pode trocar. */
export const AUTO_DESCRIPTIONS: Partial<Record<ReceiptType, string>> = {
  DEPOSIT: 'Depósito',
  UNA_DEPOSIT: 'Depósito UNA Markets',
  WELCOME: 'Alavancagem boas-vindas',
  EVENT: 'Alavancagem evento',
  GAS: 'Gás',
  LIVE: 'Repasse Live',
};
// "Alavancagem" e "Repasse" eram as descrições prontas de antes: também contam como não mexidas.
const AUTO_DESCRIPTION_VALUES = [...Object.values(AUTO_DESCRIPTIONS), 'Alavancagem', 'Repasse'];

/**
 * O que muda no rascunho ao escolher o tipo. A descrição pronta entra quando
 * ela está vazia, e sai quando a pessoa troca de tipo sem ter mexido nela.
 * O valor do comprovante é o do tipo (vendido, depositado, gás); a alavancagem
 * tem o depositado e, realizada, a buscada. Entrar ou sair da alavancagem zera
 * o valor — a buscada não vira vendido sem ninguém ver — e sair zera o depositado
 * (no servidor também: ele zera o depositado, a buscada e a situação fora da alavancagem).
 */
function patchForType(draft: ReceiptDraft, type: ReceiptType): Partial<ReceiptDraft> {
  const current = draft.description.trim();
  const untouched = current === '' || AUTO_DESCRIPTION_VALUES.includes(current);
  const base = { leverageType: type, ...(untouched ? { description: AUTO_DESCRIPTIONS[type] ?? '' } : {}), leverageAmount: 0 };
  // O depositado acompanha o depósito: do sem tipo para a alavancagem ele vai para
  // o campo do depositado (a buscada espera a alavancagem realizada), e volta no caminho contrário.
  if (isDeposit(draft.leverageType) && isAlavancagem(type)) {
    return { ...base, depositAmount: draft.soldAmount, soldAmount: 0 };
  }
  if (isAlavancagem(draft.leverageType) && isDeposit(type)) {
    return { ...base, soldAmount: draft.depositAmount, depositAmount: 0 };
  }
  return {
    ...base,
    ...(isAlavancagem(type) !== isAlavancagem(draft.leverageType) && draft.leverageType ? { soldAmount: 0 } : {}),
    ...(isAlavancagem(type) ? {} : { depositAmount: 0 }),
  };
}

/** Como o valor, o dia e quem lança se chamam em cada tipo. */
interface TypeWording {
  value: string;
  valueHint?: string;
  missingValue: string;
  description: string;
  descriptionPlaceholder: string;
  missingDescription: string;
  day: string;
  missingDay: string;
  member: string;
  memberHint: string;
}

const SALE_WORDING: TypeWording = {
  value: 'Valor vendido (R$) *',
  missingValue: 'Informe o valor vendido.',
  description: 'Descrição do produto *',
  descriptionPlaceholder: 'Ex.: Mentoria Premium — turma de março',
  missingDescription: 'Descreva o produto vendido.',
  day: 'Dia da venda *',
  missingDay: 'Informe o dia da venda.',
  member: 'Membro que vendeu *',
  memberHint: 'É quem recebe a venda no ranking.',
};

const ALAVANCAGEM_WORDING: TypeWording = {
  value: 'Alavancagem buscada (R$) *',
  valueHint: 'Quanto foi buscado. Não entra no valor vendido.',
  missingValue: 'Informe a alavancagem buscada.',
  description: 'Descrição *',
  descriptionPlaceholder: 'Ex.: Alavancagem — João',
  missingDescription: 'Descreva a alavancagem.',
  day: 'Dia da alavancagem *',
  missingDay: 'Informe o dia da alavancagem.',
  member: 'Membro responsável *',
  memberHint: 'A alavancagem conta para ele.',
};

function wordingOf(type: ReceiptType | ''): TypeWording {
  if (isGas(type)) {
    return {
      value: 'Valor do gás (R$) *',
      valueHint: 'Fica fora do vendido e do salário.',
      missingValue: 'Informe o valor do gás.',
      description: 'Descrição *',
      descriptionPlaceholder: 'Ex.: Gás semanal',
      missingDescription: 'Descreva o lançamento (ex.: Gás).',
      day: 'Dia do gás *',
      missingDay: 'Informe o dia do gás.',
      member: 'Membro que lançou *',
      memberHint: 'O gás aparece no bloco dele no painel.',
    };
  }
  if (isUnaDeposit(type)) {
    return {
      value: 'Valor depositado na UNA (R$) *',
      valueHint: 'Fica à parte: fora do vendido e do depositado da outra corretora.',
      missingValue: 'Informe o valor depositado na UNA Markets.',
      description: 'Descrição *',
      descriptionPlaceholder: 'Ex.: Depósito UNA Markets',
      missingDescription: 'Descreva o depósito.',
      day: 'Dia do depósito *',
      missingDay: 'Informe o dia do depósito.',
      member: 'Membro responsável *',
      memberHint: 'O depósito conta para ele.',
    };
  }
  if (isDeposit(type)) {
    return {
      value: 'Valor depositado (R$) *',
      valueHint: 'Fica pendente até alguém dizer se foi de boas-vindas ou de evento.',
      missingValue: 'Informe o valor depositado.',
      description: 'Descrição *',
      descriptionPlaceholder: 'Ex.: Primeiro depósito',
      missingDescription: 'Descreva o depósito.',
      day: 'Dia do depósito *',
      missingDay: 'Informe o dia do depósito.',
      member: 'Membro responsável *',
      memberHint: 'O depósito conta para ele.',
    };
  }
  return isAlavancagem(type) ? ALAVANCAGEM_WORDING : SALE_WORDING;
}

export const emptyDraft = (overrides: Partial<ReceiptDraft> = {}): ReceiptDraft => ({
  description: '',
  soldAmount: 0,
  leverageType: '',
  leverageAmount: 0,
  depositAmount: 0,
  leverageStatus: 'PENDING',
  date: todayISO(),
  time: nowHHMM(),
  leadId: '',
  leadName: '',
  memberId: '',
  note: '',
  ...overrides,
});

export const draftFromReceipt = (receipt: Receipt): ReceiptDraft => ({
  description: receipt.description,
  soldAmount: receipt.soldAmount,
  leverageType: receipt.leverageType ?? '',
  leverageAmount: receipt.leverageAmount,
  depositAmount: receipt.depositAmount,
  leverageStatus: receipt.leverageStatus ?? 'PENDING',
  date: receipt.date,
  time: receipt.time,
  leadId: receipt.leadId ?? '',
  leadName: receipt.leadName ?? '',
  memberId: receipt.memberId,
  note: receipt.note ?? '',
});

/** Primeiro problema do rascunho, em português; `null` quando dá para enviar. */
export function validateDraft(
  draft: ReceiptDraft,
  options: {
    requireMember: boolean;
    requireType: boolean;
    requireLead: boolean;
    requireDeposit: boolean;
    /** A buscada da realizada; na edição, só quando ela ou a situação muda (a realizada antiga sem buscada segue editável). */
    requireSought?: boolean;
    /** Na edição, a observação de antes: a senha que já estava lá não trava o resto (a regra vale para o que mudou). */
    originalNote?: string;
  },
): string | null {
  if (options.requireType && !draft.leverageType) {
    return 'Escolha o tipo do comprovante.';
  }
  const missingLead = options.requireLead && requiresLead(draft.leverageType) && !draft.leadId;
  // O repasse só tem a própria seção (pendente ou feito), que o modal confere.
  if (isTransfer(draft.leverageType)) {
    if (missingLead) return 'Escolha o cliente do repasse.';
    return options.requireMember && !draft.memberId ? 'Escolha o membro que fez o repasse.' : null;
  }
  const wording = wordingOf(draft.leverageType);
  if (draft.description.trim().length < 2) return wording.missingDescription;
  if (options.requireDeposit && isAlavancagem(draft.leverageType) && draft.depositAmount <= 0) return 'Informe o valor depositado.';
  // Na alavancagem o valor é a buscada: só a realizada tem.
  const needsValue = isAlavancagem(draft.leverageType) ? draft.leverageStatus === 'DONE' && options.requireSought !== false : true;
  if (needsValue && draft.soldAmount <= 0) return wording.missingValue;
  if (missingLead) return isGas(draft.leverageType) ? 'Escolha o cliente que colocou o gás.' : 'Escolha o cliente: só a venda pode ficar sem cliente.';
  if (draft.note.trim() !== (options.originalNote ?? '').trim() && hasPasswordInNote(draft.note)) return PASSWORD_NOTE_WARNING;
  if (!draft.date) return wording.missingDay;
  if (options.requireMember && !draft.memberId) return 'Escolha o membro responsável.';
  return null;
}

interface ReceiptFieldsProps {
  draft: ReceiptDraft;
  onChange: (patch: Partial<ReceiptDraft>) => void;
  isAdmin: boolean;
  autoFocus?: boolean;
  /** Venda da Ephra ou parcela em cripto: descrição, valor, data, tipo e membro vêm de lá e ficam travados. */
  locked?: boolean;
  /** O aviso mostrado com os campos travados. */
  lockedNote?: string;
  /** O que entra logo depois do tipo — no cadastro, o arquivo do comprovante. */
  afterType?: ReactNode;
  /** No cadastro, o tipo vem primeiro: o resto só aparece depois de escolhido. */
  waitForType?: boolean;
}

const PASSWORD_NOTE_WARNING =
  'Não escreva senha na observação: ela fica aberta para quem vê o comprovante. Use o campo "Senha da conta" do cliente, guardado com criptografia.';

const EPHRA_LOCKED_NOTE = 'Venda da Ephra: só a observação e o cliente mudam aqui. O resto vem da Ephra.';

/**
 * Os campos do comprovante — os mesmos no cadastro e na edição. O tipo vem
 * primeiro e decide o resto: o repasse não tem produto, valor vendido, dia nem
 * cliente — só a seção do repasse, que fica com o modal.
 */
export function ReceiptFields({
  draft, onChange, isAdmin, autoFocus, locked = false, lockedNote = EPHRA_LOCKED_NOTE, afterType, waitForType = false,
}: ReceiptFieldsProps) {
  const groupName = useId();
  const members = useMemberOptions();
  const transfer = isTransfer(draft.leverageType);
  const waiting = waitForType && !draft.leverageType;
  const wording = wordingOf(draft.leverageType);
  // Só o comprovante que já nasceu venda mantém a opção.
  const [saleKept] = useState(() => draft.leverageType === 'SALE');
  const typeOptions = saleKept ? TYPE_OPTIONS : TYPE_OPTIONS.filter((option) => option.value !== 'SALE');

  const memberField = isAdmin ? (
    <Field
      label={transfer ? 'Membro que fez o repasse *' : wording.member}
      hint={transfer ? 'O repasse conta para ele.' : wording.memberHint}
    >
      <select className="inp" value={draft.memberId} onChange={(event) => onChange({ memberId: event.target.value })} disabled={locked}>
        <option value="">Selecione o membro…</option>
        {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
      </select>
    </Field>
  ) : null;

  return (
    <>
      {locked ? (
        <p className="muted" role="note" style={{ fontSize: 12.5, margin: 0 }}>
          {lockedNote}
        </p>
      ) : null}

      <fieldset className="field" style={{ margin: 0, padding: 0, border: 0 }} disabled={locked}>
        <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>Tipo *</legend>
        <div className="choice-group">
          {typeOptions.map((option, index) => (
            <label key={option.value} className="choice">
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={draft.leverageType === option.value}
                onChange={() => onChange(patchForType(draft, option.value))}
                autoFocus={waiting && autoFocus && index === 0}
              />
              {option.label}
            </label>
          ))}
        </div>
        {saleKept ? null : <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>{SALE_CLOSED_NOTE}</p>}
      </fieldset>

      {waiting ? (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Escolha o tipo para continuar.</p>
      ) : (
        <>
          {afterType}
          {transfer ? (
            <>
              {memberField}
              <LeadField draft={draft} onChange={onChange} isAdmin={isAdmin} />
            </>
          ) : (
            <SaleFields draft={draft} onChange={onChange} autoFocus={autoFocus} locked={locked} isAdmin={isAdmin} memberField={memberField} />
          )}
        </>
      )}
    </>
  );
}

interface SaleFieldsProps {
  draft: ReceiptDraft;
  onChange: (patch: Partial<ReceiptDraft>) => void;
  isAdmin: boolean;
  autoFocus?: boolean;
  locked: boolean;
  memberField: ReactNode;
}

/** O cliente: obrigatório em todo tipo, menos na venda. */
function LeadField({ draft, onChange, isAdmin }: Pick<SaleFieldsProps, 'draft' | 'onChange' | 'isAdmin'>) {
  return (
    <LeadPicker
      value={draft.leadId}
      valueName={draft.leadName}
      isAdmin={isAdmin}
      required={requiresLead(draft.leverageType)}
      fromBase
      allowCreate
      {...(isAdmin && draft.memberId ? { newLeadOwnerId: draft.memberId } : {})}
      onChange={(lead) => onChange({ leadId: lead?.id ?? '', leadName: lead?.name ?? '' })}
    />
  );
}

/**
 * Os campos de quem tem valor: depósito, venda, alavancagem e gás. Na
 * alavancagem o valor é o depositado; a buscada só aparece com ela realizada.
 */
function SaleFields({ draft, onChange, isAdmin, autoFocus, locked, memberField }: SaleFieldsProps) {
  const wording = wordingOf(draft.leverageType);
  const alavancagem = isAlavancagem(draft.leverageType);
  const statusGroup = useId();
  const passwordInNote = hasPasswordInNote(draft.note);

  return (
    <>
      <Field label={wording.description}>
        <input
          className="inp"
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder={wording.descriptionPlaceholder}
          maxLength={300}
          autoFocus={autoFocus && !locked}
          disabled={locked}
        />
      </Field>

      <div className="grid grid3">
        {alavancagem ? (
          <Field label="Valor depositado (R$) *" hint="Entra no capital do cliente na alavancagem.">
            <MoneyInput value={draft.depositAmount} onChange={(depositAmount) => onChange({ depositAmount })} disabled={locked} />
          </Field>
        ) : (
          <Field label={wording.value} hint={wording.valueHint}>
            <MoneyInput value={draft.soldAmount} onChange={(soldAmount) => onChange({ soldAmount })} disabled={locked} />
          </Field>
        )}
        <Field label={wording.day}>
          <input className="inp" type="date" value={draft.date} onChange={(event) => onChange({ date: event.target.value })} disabled={locked} />
        </Field>
        <Field label="Hora">
          <input className="inp" type="time" value={draft.time} onChange={(event) => onChange({ time: event.target.value })} disabled={locked} />
        </Field>
      </div>

      {alavancagem ? (
        <fieldset className="field" style={{ margin: 0, padding: 0, border: 0 }} disabled={locked}>
          <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>Alavancagem</legend>
          <div className="choice-group">
            {LEVERAGE_STATUSES.map((status) => (
              <label key={status} className="choice">
                <input
                  type="radio"
                  name={statusGroup}
                  value={status}
                  checked={draft.leverageStatus === status}
                  // A buscada só existe realizada: sair de "realizada" a zera (o servidor também).
                  onChange={() => onChange({ leverageStatus: status, ...(status === 'DONE' ? {} : { soldAmount: 0 }) })}
                />
                {LEVERAGE_STATUS_LABELS[status]}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {alavancagem && draft.leverageStatus === 'DONE' ? (
        <div className="grid grid3">
          <Field label={wording.value} hint={wording.valueHint}>
            <MoneyInput value={draft.soldAmount} onChange={(soldAmount) => onChange({ soldAmount })} disabled={locked} />
          </Field>
        </div>
      ) : null}

      {isAdmin ? memberField : null}

      <LeadField draft={draft} onChange={onChange} isAdmin={isAdmin} />

      <Field
        label="Observação"
        {...(passwordInNote
          ? { error: 'Não escreva senha aqui: ela fica aberta para quem vê o comprovante. Use o campo "Senha da conta".' }
          : {})}
      >
        <input
          className="inp"
          value={draft.note}
          onChange={(event) => onChange({ note: event.target.value })}
          placeholder="Opcional — sem senhas"
          maxLength={500}
        />
      </Field>
    </>
  );
}
